import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSidebarContext } from '@/app/layouts/layout-context';
import { SidebarItem } from './SidebarItem';
import { SidebarMenu } from './SidebarMenu';
import { CompanyDropdown } from './CompanyDropdown';
import { ROUTES } from '@/shared/config';
import { Avatar, Tooltip } from '@heroui/react';
import { cn } from '@/shared/lib/utils';
import { useBodyScrollLock } from '@/shared/lib/hooks';

export const SidebarWrapper = () => {
  const location = useLocation();
  const { collapsed, setCollapsed } = useSidebarContext();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Lock body scroll on mobile when sidebar is open
  useBodyScrollLock(!collapsed);

  const isActive = (path: string) => {
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === ROUTES.DASHBOARD || location.pathname === ROUTES.ROOT;
    }
    return location.pathname.startsWith(path);
  };

  // Handle touch events to prevent scroll propagation
  const handleTouchStart = (e: React.TouchEvent) => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const { scrollTop } = sidebar;
    const touchY = e.touches[0].clientY;
    
    // Store initial values for touchmove
    sidebar.dataset.touchStartY = touchY.toString();
    sidebar.dataset.scrollTop = scrollTop.toString();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const touchY = e.touches[0].clientY;
    const touchStartY = parseFloat(sidebar.dataset.touchStartY || '0');
    const scrollTop = parseFloat(sidebar.dataset.scrollTop || '0');
    const { scrollHeight, clientHeight } = sidebar;

    const touchDelta = touchY - touchStartY;
    const isScrollingUp = touchDelta > 0;
    const isScrollingDown = touchDelta < 0;

    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop >= scrollHeight - clientHeight;

    // Prevent scroll propagation when reaching boundaries
    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      e.preventDefault();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div 
          className="md:hidden fixed inset-0 bg-slate-900/30 z-50 transition-opacity duration-300"
          onClick={setCollapsed}
        />
      )}

      <aside 
        ref={sidebarRef}
        className={cn(
          'h-screen z-50 transition-transform duration-200 ease-in-out',
          'w-64 bg-background border-r border-divider flex-shrink-0',
          'overflow-y-auto scrollbar-hide',
          'flex flex-col py-6 px-4',
          // Mobile: fixed position, toggle with collapsed state
          // Desktop: sticky position at top
          'fixed md:sticky md:top-0',
          // Mobile: hide when collapsed, show when not collapsed
          // Desktop: always show (md:translate-x-0)
          collapsed ? '-translate-x-full md:translate-x-0' : 'translate-x-0'
        )}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {/* Header */}
        <div className="flex gap-4 items-center px-6 sm:mb-2 md:mb-8">
          <CompanyDropdown />
        </div>

        {/* Navigation Body */}
        <div className="flex flex-col gap-6 mt-8 px-2 flex-1">
          {/* Main Navigation */}
          <SidebarItem
            title="대시보드"
            icon="fa-tachometer"
            isActive={isActive(ROUTES.DASHBOARD)}
            href={ROUTES.DASHBOARD}
          />
          
          <SidebarMenu title="주요 메뉴">
            <SidebarItem
              title="폰보드 관리"
              icon="fa-desktop"
              isActive={isActive(ROUTES.PHONEBOARDS)}
              href={ROUTES.PHONEBOARDS}
            />
            <SidebarItem
              title="작업 관리"
              icon="fa-tasks"
              isActive={isActive(ROUTES.TASKS)}
              href={ROUTES.TASKS}
            />
            <SidebarItem
              title="SNS 계정 관리"
              icon="fa-bar-chart"
              isActive={isActive(ROUTES.SNS_ACCOUNTS)}
              href={ROUTES.SNS_ACCOUNTS}
            />
            <SidebarItem
              title="알림"
              icon="fa-bell"
              isActive={isActive(ROUTES.NOTIFICATIONS)}
              href={ROUTES.NOTIFICATIONS}
            />
          </SidebarMenu>
        </div>
      </aside>
    </>
  );
};